import type { Missed } from './outage';

/**
 * The reads one page's regions found unanswered (D163): a package's files, a file, the files a comparison
 * shows. Each region says it is unavailable in its own place and reports its read here, and the page says
 * them all in its one line. A region that answered, or left the page, takes its read out.
 */
export class Missing {
	#reads = new Map<string, Missed>();
	#listeners = new Set<() => void>();
	#list: Missed[] = [];

	/** The reads unanswered now: the same array until one comes or goes, as `useSyncExternalStore` needs */
	get reads(): Missed[] {
		return this.#list;
	}

	/**
	 * Records a read nobody answered, or takes it out (`null`). The same refusal again only refreshes its
	 * `run`, so a region that draws again tells nobody.
	 */
	set(key: string, missed: Missed | null): void {
		const known = this.#reads.get(key);
		if (!missed) {
			if (!known) return;
			this.#reads.delete(key);
		} else if (known && known.refusal === missed.refusal && known.title === missed.title) {
			known.run = missed.run;
			return;
		} else this.#reads.set(key, { ...missed, key });
		this.#list = [...this.#reads.values()];
		for (const listener of [...this.#listeners]) listener();
	}

	/** Hears every read that comes or goes; returns the release */
	listen = (listener: () => void): (() => void) => {
		this.#listeners.add(listener);
		return () => void this.#listeners.delete(listener);
	};
}
